import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  PrimaryButton,
  Table,
} from "widgets";
import {
  useFetchAllBanks,
  useFetchOrderDetails,
  useFetchOrderSummaryPDF,
  useFetchPaymentReceipt,
  useFetchTaxInvoicePDF,
  useUpdateBankTransferStatus,
  useUpdateChequeStatus,
} from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import Loader from "shared/Loader/Loader";
import { formatINRCurrency } from "utils/formatINRCurrency";
import {
  BANK_TRANSFER_STATUS_IDS,
  BANK_TRANSFER_UPDATE_RELOAD_DELAY_IN_MS,
  CASH_PAYMENT_METHOD_ID,
  CHEQUE_STATUS_IDS,
  PAYMENT_STATUS,
} from "constants/constants";
import { formatReadableDate } from "utils/formatDate";
import ErrorButton from "widgets/Buttons/ErrorButton";

import styles from "./OrderDetails.module.scss";

const getColumns = () => {
  return [
    { label: "Reference No.", field: "referenceNo" },
    { label: "Amount", field: "amount" },
    { label: "Status", field: "status" },
    { label: "Method", field: "method" },
    { label: "Payment Date", field: "payment_date" },
    { label: "Bank Details", field: "bankDetails" },
    { label: "Action", field: "action" },
  ];
};

const updateChequePayment = (
  openSnackbar,
  selectedPayment,
  newStatusId,
  updateChequeStatus,
  setDialogContent,
) => {
  const cashierCRNId = localStorage.getItem("cashierCRNId");
  if (!cashierCRNId) {
    openSnackbar("Something went wrong.", "error");
    return;
  }
  const payload = {
    payment_id: selectedPayment.id,
    marked_by: cashierCRNId,
    updated_cheque_status_id: newStatusId,
  };
  updateChequeStatus(payload, {
    onSuccess: (response) => {
      setDialogContent(null);
      openSnackbar("Cheque Status Successfully Updated", "success");
      setTimeout(() => {
        window.location.reload();
      }, BANK_TRANSFER_UPDATE_RELOAD_DELAY_IN_MS);
    },
    onError: (error) => {
      openSnackbar(`${error}`, "error");
    },
  });
};

const updateBankTransferPayment = (
  openSnackbar,
  selectedPayment,
  newStatusId,
  updateBankTransferStatus,
  setDialogContent,
) => {
  const cashierCRNId = localStorage.getItem("cashierCRNId");
  if (!cashierCRNId) {
    openSnackbar("Something went wrong.", "error");
    return;
  }
  const payload = {
    payment_id: selectedPayment.id,
    marked_by: cashierCRNId,
    new_bank_transfer_status_id: newStatusId,
  };
  updateBankTransferStatus(payload, {
    onSuccess: (response) => {
      setDialogContent(null);
      openSnackbar("Bank Transfer Status Successfully Updated", "success");
      setTimeout(() => {
        window.location.reload();
      }, BANK_TRANSFER_UPDATE_RELOAD_DELAY_IN_MS);
    },
    onError: (error) => {
      openSnackbar(`${error}`, "error");
    },
  });
};

const handleUpdatePaymentStatus = (
  openSnackbar,
  selectedPayment,
  newStatusId,
  updateChequeStatus,
  setDialogContent,
  updateBankTransferStatus,
) => {
  if (
    selectedPayment.cheque_details &&
    Object.keys(selectedPayment.cheque_details).length !== 0
  ) {
    updateChequePayment(
      openSnackbar,
      selectedPayment,
      newStatusId,
      updateChequeStatus,
      setDialogContent,
    );
    return;
  }

  if (
    selectedPayment.bank_transfer_details &&
    Object.keys(selectedPayment.bank_transfer_details).length !== 0
  ) {
    updateBankTransferPayment(
      openSnackbar,
      selectedPayment,
      newStatusId,
      updateBankTransferStatus,
      setDialogContent,
    );
    return;
  }
};

const handleStatusChange = (
  payment,
  setSelectedPayment,
  setDialogContent,
  setNewStatusId,
  statusId,
  confirmationMessage,
) => {
  setSelectedPayment(payment);
  setNewStatusId(statusId);
  setDialogContent(confirmationMessage);
};

const getReferenceNumber = (payment) => {
  if (
    payment.cheque_details &&
    Object.keys(payment.cheque_details).length !== 0
  ) {
    return payment.cheque_details.reference_number;
  }

  if (
    payment.transaction_details &&
    Object.keys(payment.transaction_details).length !== 0
  ) {
    return payment.transaction_details.transaction_id;
  }

  if (payment.loan_details && Object.keys(payment.loan_details).length !== 0) {
    return payment.loan_details.loan_id;
  }

  if (
    payment.bank_transfer_details &&
    Object.keys(payment.bank_transfer_details).length !== 0
  ) {
    return payment.bank_transfer_details.reference_transaction_id;
  }

  return "N/A";
};

const getBankDetailNumber = (payment, banks) => {
  if (
    payment.cheque_details &&
    Object.keys(payment.cheque_details).length !== 0
  ) {
    return payment.cheque_details.bank.name;
  }

  if (
    payment.bank_transfer_details &&
    Object.keys(payment.bank_transfer_details).length !== 0 &&
    payment.bank_transfer_details.bank_id !== null
  ) {
    const bankId = payment.bank_transfer_details.bank_id;
    const bank = banks.find((b) => b.id === bankId);

    const bankName = bank ? bank.name : "N/A";
    return bankName;
  }
  return "N/A";
};

const renderChequeActions = (
  payment,
  setSelectedPayment,
  setDialogContent,
  setNewStatusId,
) => (
  <div className={styles.actionButtons}>
    <PrimaryButton
      onClick={() =>
        handleStatusChange(
          payment,
          setSelectedPayment,
          setDialogContent,
          setNewStatusId,
          CHEQUE_STATUS_IDS.DISBURSED,
          "Are you sure you want to mark the cheque as Realized? This action cannot be undone.",
        )
      }
    >
      Cheque Realized
    </PrimaryButton>
    <ErrorButton
      onClick={() =>
        handleStatusChange(
          payment,
          setSelectedPayment,
          setDialogContent,
          setNewStatusId,
          CHEQUE_STATUS_IDS.FAILED,
          "Are you sure you want to mark the cheque as Failed? This action cannot be undone.",
        )
      }
    >
      Cheque Failed
    </ErrorButton>
  </div>
);

const renderBankTransferActions = (
  payment,
  setSelectedPayment,
  setDialogContent,
  setNewStatusId,
) => (
  <div className={styles.actionButtons}>
    <ErrorButton
      onClick={() =>
        handleStatusChange(
          payment,
          setSelectedPayment,
          setDialogContent,
          setNewStatusId,
          BANK_TRANSFER_STATUS_IDS.FAILED,
          "Are you sure you want to mark the Bank Transfer as Failed? This action cannot be undone.",
        )
      }
    >
      Bank Transfer Failed
    </ErrorButton>
    <PrimaryButton
      onClick={() =>
        handleStatusChange(
          payment,
          setSelectedPayment,
          setDialogContent,
          setNewStatusId,
          BANK_TRANSFER_STATUS_IDS.DISBURSED,
          "Are you sure you want to mark the Bank Transfer as Approved? This action cannot be undone.",
        )
      }
    >
      Bank Transfer Approved
    </PrimaryButton>
  </div>
);
const handleOnClickDownloadPaymentReceipt = (
  paymentId,
  fetchPaymentReceipt,
  openSnackbar,
) => {
  if (paymentId) {
    fetchPaymentReceipt(paymentId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Payment receipt downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching receipt", error);
        openSnackbar(
          error.message || "Error fetching payment receipt",
          "error",
        );
      });
  } else {
    console.error("No payment ID provided");
  }
};
const getAction = (
  payment,
  setSelectedPayment,
  setDialogContent,
  setNewStatusId,
  fetchPaymentReceipt,
  openSnackbar,
) => {
  const chequeStatusId = payment?.cheque_details?.status?.id;
  const bankTransferStatus = payment?.bank_transfer_details?.status?.id;

  if (chequeStatusId === CHEQUE_STATUS_IDS.VERIFIED) {
    return renderChequeActions(
      payment,
      setSelectedPayment,
      setDialogContent,
      setNewStatusId,
    );
  }

  if (bankTransferStatus === BANK_TRANSFER_STATUS_IDS.CREATED) {
    return renderBankTransferActions(
      payment,
      setSelectedPayment,
      setDialogContent,
      setNewStatusId,
    );
  }
  if (payment.has_payment_receipt) {
    return (
      <PrimaryButton
        onClick={() =>
          handleOnClickDownloadPaymentReceipt(
            payment.id,
            fetchPaymentReceipt,
            openSnackbar,
          )
        }
      >
        Download Receipt
      </PrimaryButton>
    );
  }

  return <div>N/A</div>;
};

const getPaymentStatusName = (payment) => {
  if (
    payment.status.id === PAYMENT_STATUS.DISBURSED &&
    payment.method.id === CASH_PAYMENT_METHOD_ID
  ) {
    return "ACKNOWLEDGED";
  }
  return payment.status.name;
};

const getRows = (
  payments,
  setSelectedPayment,
  setDialogContent,
  setNewStatusId,
  fetchPaymentReceipt,
  banks,
  hasPaymentHead,
  isFetchPaymentReceiptLoading,
  openSnackbar,
) => {
  const sortedPayments = [...payments].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );

  return sortedPayments.map((payment) => ({
    referenceNo: getReferenceNumber(payment),
    amount: formatINRCurrency(payment.amount),
    method: payment.method.name,
    status: getPaymentStatusName(payment),
    payment_date: formatReadableDate(payment.date, { includeTime: true }),
    bankDetails: getBankDetailNumber(payment, banks),
    action: (
      <div className={styles.actionButtons}>
        {getAction(
          payment,
          setSelectedPayment,
          setDialogContent,
          setNewStatusId,
          fetchPaymentReceipt,
          hasPaymentHead,
          isFetchPaymentReceiptLoading,
          openSnackbar,
        )}
      </div>
    ),
  }));
};

const handleDialogClose = (setDialogContent) => () => {
  setDialogContent(null);
};
const handleClickGetOrderSummaryPDFButton = (
  orderId,
  refetchOrderSummaryPDF,
  openSnackbar,
) => {
  if (!!orderId) {
    refetchOrderSummaryPDF()
      .then((response) => {
        if (response && response.status === "success") {
          const order_summary_response = response.data;
          if (!!order_summary_response.message) {
            openSnackbar(order_summary_response.message);
          }
          if (!!order_summary_response.signed_url) {
            const link = document.createElement("a");
            link.href = order_summary_response.signed_url;
            link.download = `order_summary_${orderId}.pdf`;
            link.click();
            openSnackbar("Order summary downloaded successfully!", "success");
          }
        } else {
          openSnackbar(
            response.error.message || "Error fetching Order Summary",
          );
        }
      })
      .catch((error) => {
        openSnackbar("Error fetching Order Summary");
      });
  }
};

const handleClickGetTaxInvoiceButton = (
  orderId,
  fetchTaxInvoicePDF,
  openSnackbar,
) => {
  if (orderId) {
    fetchTaxInvoicePDF(orderId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Tax Invoice downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching tax invoice", error);
        openSnackbar(error.message || "Error fetching tax invoice", "error");
      });
  } else {
    console.error("No order ID provided");
  }
};
const OrderDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dialogContent, setDialogContent] = useState(null);
  const { openSnackbar } = useSnackbarContext();

  const orderId = searchParams.get("order_id");

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [newStatusId, setNewStatusId] = useState("");

  const {
    isPending: isOrderDetailsLoading,
    data: orderDetails,
    isError: isErrorOnOrderDetails,
    error: errorMessageOnOrderDetails,
  } = useFetchOrderDetails(orderId);

  const {
    isPending: isFetchBanksLoading,
    data: banks,
    isError: isErrorOnFetchBanks,
    error: errorMessageOnFetchBanks,
  } = useFetchAllBanks();

  const { mutate: updateChequeStatus, isPending: isUpdateChequeStatusLoading } =
    useUpdateChequeStatus();

  const {
    mutate: updateBankTransferStatus,
    isPending: isUpdateBankTransferStatusLoading,
  } = useUpdateBankTransferStatus();

  const {
    isLoading: isOrderSummaryPDFLoading,
    refetch: refetchOrderSummaryPDF,
  } = useFetchOrderSummaryPDF(orderId);

  const { fetchTaxInvoicePDF, isLoading: isTaxInvoicePDFLoading } =
    useFetchTaxInvoicePDF();

  const { fetchPaymentReceipt, isLoading: isFetchPaymentReceiptLoading } =
    useFetchPaymentReceipt();

  if (isOrderDetailsLoading) {
    return <Loader message="Fetching Order details..." />;
  }

  if (isFetchBanksLoading) {
    return <Loader />;
  }

  if (isErrorOnOrderDetails) {
    return <ErrorMessage>{errorMessageOnOrderDetails.message}</ErrorMessage>;
  }

  if (isErrorOnFetchBanks) {
    return <ErrorMessage>{errorMessageOnFetchBanks.message}</ErrorMessage>;
  }

  const payments = orderDetails.payment_details;

  const packageDetails = orderDetails.package_details;
  const subscriptions = packageDetails?.subscriptions;
  const hasPaymentHead =
    Array.isArray(subscriptions) &&
    subscriptions.some(
      (sub) => Array.isArray(sub.payment_heads) && sub.payment_heads.length > 0,
    );

  const orderStatus = orderDetails.status.id;

  return (
    <div className={styles.mainContainer}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <ArrowBackIcon fontSize="small" />
        Back
      </button>
      <div className={styles.headingContainer}>
        <div className={styles.heading}>Orders Details</div>
      <div className={styles.buttonContainer}>
        {orderDetails.has_order_summary && (
          <PrimaryButton
            disabled={isOrderSummaryPDFLoading}
            onClick={() =>
              handleClickGetOrderSummaryPDFButton(
                orderId,
                refetchOrderSummaryPDF,
                openSnackbar,
              )
            }
          >
            Get Order Summary
          </PrimaryButton>
        )}
        {orderDetails.has_grand_tax_invoice && (
          <PrimaryButton
            disabled={isTaxInvoicePDFLoading}
            onClick={() =>
              handleClickGetTaxInvoiceButton(
                orderId,
                fetchTaxInvoicePDF,
                openSnackbar,
              )
            }
          >
            Get Tax Invoice
          </PrimaryButton>
        )}
      </div>
      </div>
      <div className={styles.OrderDetails}>
        <DetailDisplay title="Order Id" value={orderDetails.order_id} />
        {orderDetails.application_number != null && (
          <DetailDisplay
            title="Application No"
            value={orderDetails.application_number}
          />
        )}
        <DetailDisplay
          title="Billing Name"
          value={orderDetails.address_details.billing_address.recipient_name}
        />
        <DetailDisplay
          title="Billing Phone Number"
          value={orderDetails.address_details.billing_address.masked_phone_no}
        />
        <DetailDisplay
          title="Total Order Amount"
          value={formatINRCurrency(orderDetails.amount)}
        />
        <DetailDisplay
          title="Pending Amount"
          value={formatINRCurrency(
            orderDetails.inr_conversion_amount -
              orderDetails.inr_conversion_amount_paid,
          )}
        />
        <DetailDisplay
          title="Order Punch Date"
          value={new Date(orderDetails.order_created_date).toLocaleDateString()}
        />
      </div>
      <div className={styles.heading}>Payments</div>
      {payments.length > 0 ? (
        <Table
          columns={getColumns()}
          rows={getRows(
            payments,
            setSelectedPayment,
            setDialogContent,
            setNewStatusId,
            fetchPaymentReceipt,
            banks,
            hasPaymentHead,
            isFetchPaymentReceiptLoading,
            openSnackbar,
          )}
        />
      ) : (
        <ErrorMessage>No payments found for this Order.</ErrorMessage>
      )}
      <Dialog
        open={dialogContent !== null}
        handleClose={handleDialogClose(setDialogContent)}
      >
        <div className={styles.dialogContent}>
          <span className={styles.dialogMessage}>{dialogContent}</span>
          <div className={styles.dialogActions}>
            <PrimaryButton
              onClick={() =>
                handleUpdatePaymentStatus(
                  openSnackbar,
                  selectedPayment,
                  newStatusId,
                  updateChequeStatus,
                  setDialogContent,
                  updateBankTransferStatus,
                )
              }
            >
              {isUpdateChequeStatusLoading || isUpdateBankTransferStatusLoading
                ? "Updating Status"
                : "Confirm"}
            </PrimaryButton>
            <PrimaryButton onClick={handleDialogClose(setDialogContent)}>
              Cancel
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default OrderDetails;
